import { useState } from 'react';
import { Building2, Plus } from 'lucide-react';
import Button from '../ui/Button';
import fieldWorkService from '../../services/fieldWork';
import type { LabourProvider, LabourProviderInput } from '../../types/fieldWork';
import { errorMessage } from '../../utils/apiResponse';
import FormModal, { Field, inputClass } from './FormModal';

/**
 * Contracting and labour-hire companies. Their workers are on the roster with
 * the provider set; later phases check the provider's invoice against the
 * hours and bins recorded here.
 */
export default function ProvidersPanel({ providers, canEdit, onChanged }: {
  providers: LabourProvider[];
  canEdit: boolean;
  onChanged: () => void;
}) {
  const [editing, setEditing] = useState<LabourProvider | 'new' | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-neutral-500">
          {canEdit ? 'Contracting and labour-hire companies that supply workers.' : 'Adding or editing providers needs a Farm Jobs Creator or Admin seat.'}
        </p>
        {canEdit && (
          <Button size="sm" variant="primary" onClick={() => setEditing('new')}>
            <Plus className="h-4 w-4" /> New provider
          </Button>
        )}
      </div>
      {providers.length === 0 ? (
        <p className="text-sm text-neutral-500 py-10 text-center">No labour providers yet.</p>
      ) : (
        <ul className="divide-y divide-neutral-100 border border-neutral-200 rounded-lg" aria-label="Labour providers">
          {providers.map(p => (
            <li key={p.id}>
              <button
                type="button"
                disabled={!canEdit}
                onClick={() => setEditing(p)}
                className="w-full text-left px-3 py-3 flex items-start gap-3 disabled:cursor-default"
              >
                <Building2 className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-neutral-900">{p.name}</span>
                    {!p.active && <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-200 text-neutral-600">Inactive</span>}
                  </div>
                  <p className="text-xs text-neutral-500 mt-0.5">
                    {p.worker_count} {p.worker_count === 1 ? 'worker' : 'workers'}
                    {p.contact_name && ` · ${p.contact_name}`}
                    {p.phone && ` · ${p.phone}`}
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <ProviderFormModal
          provider={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onChanged(); }}
        />
      )}
    </div>
  );
}

function ProviderFormModal({ provider, onClose, onSaved }: {
  provider: LabourProvider | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<LabourProviderInput>({
    name: provider?.name ?? '',
    contact_name: provider?.contact_name ?? '',
    phone: provider?.phone ?? '',
    email: provider?.email ?? '',
    business_number: provider?.business_number ?? '',
    notes: provider?.notes ?? '',
    active: provider?.active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = <K extends keyof LabourProviderInput>(k: K, v: LabourProviderInput[K]) => setForm(f => ({ ...f, [k]: v }));

  const submit = async () => {
    setSaving(true);
    setError(null);
    const payload: LabourProviderInput = { ...form, name: form.name.trim() };
    if (!provider) delete payload.active;
    try {
      if (provider) await fieldWorkService.updateProvider(provider.id, payload);
      else await fieldWorkService.createProvider(payload);
      onSaved();
    } catch (e) {
      setError(errorMessage(e, 'Could not save the provider'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      title={provider ? 'Edit labour provider' : 'New labour provider'}
      onClose={onClose}
      onSubmit={submit}
      submitting={saving}
      submitDisabled={!form.name.trim()}
      error={error}
    >
      <Field label="Name" htmlFor="lp-name">
        <input id="lp-name" className={inputClass} value={form.name} onChange={e => set('name', e.target.value)} autoFocus />
      </Field>
      <Field label="Business number (ABN or equivalent)" htmlFor="lp-bn">
        <input id="lp-bn" className={inputClass} value={form.business_number ?? ''} onChange={e => set('business_number', e.target.value)} />
      </Field>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Contact" htmlFor="lp-contact">
          <input id="lp-contact" className={inputClass} value={form.contact_name ?? ''} onChange={e => set('contact_name', e.target.value)} />
        </Field>
        <Field label="Phone" htmlFor="lp-phone">
          <input id="lp-phone" type="tel" className={inputClass} value={form.phone ?? ''} onChange={e => set('phone', e.target.value)} />
        </Field>
      </div>
      <Field label="Email" htmlFor="lp-email">
        <input id="lp-email" type="email" className={inputClass} value={form.email ?? ''} onChange={e => set('email', e.target.value)} />
      </Field>
      <Field label="Notes" htmlFor="lp-notes">
        <textarea id="lp-notes" rows={2} className={inputClass} value={form.notes ?? ''} onChange={e => set('notes', e.target.value)} />
      </Field>
      {provider && (
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.active ?? true} onChange={e => set('active', e.target.checked)}
            className="rounded border-neutral-300 text-indigo-600 focus:ring-indigo-200" />
          <span className="text-neutral-700">Active</span>
        </label>
      )}
    </FormModal>
  );
}
